import { DEFAULT_OPTIONS } from "./constants.js";
import type { Game, RatingOptions } from "./types.js";

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Convert a scoreline into a continuous outcome score for side A.
 *
 * Standard Glicko-2 takes a binary win/loss, which throws away most of the information
 * in a racket-sport scoreline: 21-19 and 21-3 are very different evidence. Games carry
 * most of the weight because winning is what the match is for; points carry the rest so
 * margin still speaks.
 *
 * The result is clamped away from 0 and 1, so no single result is ever treated as
 * certainty — the 21-0 that happened because someone rolled an ankle shouldn't nuke a
 * rating.
 */
export function outcomeScore(games: Game[], options: Partial<RatingOptions> = {}): number {
  const o = { ...DEFAULT_OPTIONS, ...options };

  let gamesA = 0;
  let gamesB = 0;
  let pointsA = 0;
  let pointsB = 0;

  for (const game of games) {
    pointsA += game.a;
    pointsB += game.b;
    if (game.a > game.b) gamesA++;
    else if (game.b > game.a) gamesB++;
  }

  const totalGames = gamesA + gamesB;
  const totalPoints = pointsA + pointsB;

  // A match with no decided games or no points played carries no information.
  const gameShare = totalGames === 0 ? 0.5 : gamesA / totalGames;
  const pointShare = totalPoints === 0 ? 0.5 : pointsA / totalPoints;

  const stretched = clamp(0.5 + (pointShare - 0.5) * o.pointStretch, 0, 1);
  const score = o.gameWeight * gameShare + o.pointWeight * stretched;

  return clamp(score, o.outcomeClampMin, o.outcomeClampMax);
}

/** Which side won on games. Null if the match is drawn or undecided. */
export function winnerOf(games: Game[]): "A" | "B" | null {
  let a = 0;
  let b = 0;
  for (const game of games) {
    if (game.a > game.b) a++;
    else if (game.b > game.a) b++;
  }
  if (a === b) return null;
  return a > b ? "A" : "B";
}
